import type { BadgeGlyphId } from '../types/engagement'

/**
 * Platform-neutral badge glyph geometry on a 24-unit grid, drawn as round-cap
 * strokes (stroke width ~1.75) so web (inline SVG) and mobile (react-native-svg)
 * render the same monochrome mark. `circles` are stroked; `dots` are filled.
 * Scrambl's own shapes: keep them simple, centered, and inside a 3–21 box.
 */
export interface BadgeGlyphGeometry {
  paths: readonly string[]
  circles?: readonly (readonly [cx: number, cy: number, r: number])[]
  dots?: readonly (readonly [cx: number, cy: number, r: number])[]
}

const FLAME =
  'M12 2.75c.7 2.9 2.7 4.5 3.9 6.3a6.1 6.1 0 0 1-3.9 10.95 6.1 6.1 0 0 1-3.85-10.9c.3 1.6 1.1 2.6 2.2 3.05-.2-3.3.5-6.6 1.65-9.4z'

export const BADGE_GLYPHS: Readonly<Record<BadgeGlyphId, BadgeGlyphGeometry>> =
  {
    flame: { paths: [FLAME] },
    'flame-crown': {
      paths: [
        'M12 5.5c.55 2.3 2.1 3.55 3.05 4.95a4.8 4.8 0 0 1-3.05 8.6 4.8 4.8 0 0 1-3.03-8.56c.24 1.25.87 2.05 1.73 2.4-.16-2.6.4-5.2 1.3-7.39z',
        'M7.5 21h9',
      ],
      dots: [
        [5, 5.5, 1.1],
        [19, 5.5, 1.1],
        [12, 2.4, 1.1],
      ],
    },
    'check-single': {
      paths: ['M8.4 12.3l2.5 2.5 4.8-5.1'],
      circles: [[12, 12, 8.5]],
    },
    'check-stack': {
      paths: [
        'M4 7.5l2 2 3.5-4',
        'M12.5 7.5H20',
        'M4 15.5l2 2 3.5-4',
        'M12.5 15.5H20',
      ],
    },
    layers: {
      paths: [
        'M12 3.5l8.5 4.5-8.5 4.5L3.5 8z',
        'M3.5 12l8.5 4.5 8.5-4.5',
        'M3.5 16l8.5 4.5 8.5-4.5',
      ],
    },
    mountain: {
      paths: [
        'M2.75 19.5l6.5-11 3.9 6.4 2.1-3.1 5.95 7.7z',
        'M7.6 11.3l1.65 1.3 1.6-1.3',
      ],
    },
    clock: {
      paths: ['M12 7.5V12l3 2'],
      circles: [[12, 12, 8.5]],
    },
    'clock-stack': {
      paths: ['M13 9.4v3.8l2.6 1.6', 'M4.9 15.4A8.5 8.5 0 0 1 15.4 4.9'],
      circles: [[13.25, 13.25, 7]],
    },
    shield: {
      paths: [
        'M12 3l7 2.6v5.5c0 4.5-3 8-7 9.4-4-1.4-7-4.9-7-9.4V5.6z',
        'M9 12.1l2.2 2.2 4.3-4.4',
      ],
    },
    'inbox-zero': {
      paths: [
        'M3.5 13.5l2.6-6.9a1.6 1.6 0 0 1 1.5-1.1h8.8a1.6 1.6 0 0 1 1.5 1.1l2.6 6.9v4a1.6 1.6 0 0 1-1.6 1.6H5.1a1.6 1.6 0 0 1-1.6-1.6z',
        'M3.5 13.5H8l1.5 2h5l1.5-2h4.5',
        'M9.7 9.8l1.6 1.6 3-3.1',
      ],
    },
    handshake: {
      paths: [
        'M2.75 10.25L6.5 6.75l3 1.25',
        'M21.25 10.25L17.75 7l-4.25.9-3.9 3.15a1.45 1.45 0 0 0 1.85 2.2L14 11.4l4.5 4.1',
        'M6.5 6.75l-.35 7.6 3.9 3.5a1.4 1.4 0 0 0 1.95-.05l.45-.45.6.5a1.4 1.4 0 0 0 1.95-.1l1.55-1.65',
      ],
    },
    rocket: {
      paths: [
        'M12 3c3 2 4.5 5.4 4.5 9.4L15 15.5H9l-1.5-3.1C7.5 8.4 9 5 12 3z',
        'M9 15.5l-2.5 2.4v2.6l3-1.1',
        'M15 15.5l2.5 2.4v2.6l-3-1.1',
        'M11 19.5h2',
      ],
      circles: [[12, 9.6, 1.7]],
    },
    trophy: {
      paths: [
        'M8 4.5h8v5a4 4 0 0 1-8 0z',
        'M8 6.25H5.25v1.25a3 3 0 0 0 3.1 3',
        'M16 6.25h2.75v1.25a3 3 0 0 1-3.1 3',
        'M12 13.5v3.5',
        'M8.5 20.25h7',
        'M9.75 17h4.5v3.25',
      ],
    },
    target: {
      paths: [],
      circles: [
        [12, 12, 8.5],
        [12, 12, 4.75],
      ],
      dots: [[12, 12, 1.5]],
    },
    compass: {
      paths: ['M15.6 8.4l-2.1 5.1-5.1 2.1 2.1-5.1z'],
      circles: [[12, 12, 8.5]],
    },
    flag: {
      paths: ['M5.5 21V3.75', 'M5.5 4.5h11.25l-2.5 4 2.5 4H5.5'],
    },
    medal: {
      paths: [
        'M8.25 3.5l2.6 7',
        'M15.75 3.5l-2.6 7',
        'M12 12.4l.95 1.9 2.1.3-1.5 1.45.35 2.1-1.9-1-1.9 1 .35-2.1-1.5-1.45 2.1-.3z',
      ],
      circles: [[12, 15.4, 5.1]],
    },
    sunrise: {
      paths: [
        'M3 17.5h18',
        'M7 17.5a5 5 0 0 1 10 0',
        'M12 4.5v3',
        'M5.4 9.4l1.9 1.9',
        'M18.6 9.4l-1.9 1.9',
        'M8.5 21h7',
      ],
    },
    boomerang: {
      paths: ['M19 12a7 7 0 1 1-2.05-4.95', 'M17.25 3.5v3.75H13.5'],
    },
  }
